/**
 * AdvokatCRM API Client
 *
 * Kommuniserer med Cloudflare Workers backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://advokat-crm-api.swagner.workers.dev';

class CRMApi {
  constructor(baseUrl = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;

    // Get token from localStorage
    const token = localStorage.getItem('access_token');

    console.log('🔑 API Request:', {
      url,
      method: options.method || 'GET',
      hasToken: !!token,
      tokenPreview: token ? token.substring(0, 20) + '...' : 'none'
    });

    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...options.headers,
      },
    };

    const response = await fetch(url, { ...defaultOptions, ...options });

    console.log('📡 API Response:', {
      url,
      status: response.status,
      ok: response.ok
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('❌ API Error:', error);
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // ============================================
  // SAKER
  // ============================================

  async getSaker(filters = {}) {
    const params = new URLSearchParams(filters);
    return this.request(`/api/saker?${params}`);
  }

  async getSak(sakId) {
    return this.request(`/api/saker/${sakId}`);
  }

  async createSak(sakData) {
    return this.request('/api/saker', {
      method: 'POST',
      body: JSON.stringify(sakData),
    });
  }

  async updateSak(sakId, sakData) {
    return this.request(`/api/saker/${sakId}`, {
      method: 'PUT',
      body: JSON.stringify(sakData),
    });
  }

  async deleteSak(sakId) {
    return this.request(`/api/saker/${sakId}`, {
      method: 'DELETE',
    });
  }

  // ============================================
  // FILER (SharePoint)
  // ============================================

  async getSakFiler(sakId) {
    return this.request(`/api/saker/${sakId}/filer`);
  }

  async uploadFil(sakId, fil, undermappe = 'dokumenter') {
    const formData = new FormData();
    formData.append('fil', fil);
    formData.append('undermappe', undermappe);

    return fetch(`${this.baseUrl}/api/saker/${sakId}/filer`, {
      method: 'POST',
      body: formData,
    }).then(res => {
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      return res.json();
    });
  }

  async slettFil(fileId, sakId) {
    return this.request(`/api/filer/${fileId}?sakId=${sakId}`, {
      method: 'DELETE',
    });
  }

  async getDownloadUrl(fileId, driveId) {
    return this.request(`/api/filer/${fileId}/download?driveId=${driveId}`);
  }

  // ============================================
  // KLIENTER
  // ============================================

  async getKlienter(filters = {}) {
    const params = new URLSearchParams(filters);
    return this.request(`/api/klienter?${params}`);
  }

  async getKlient(klientId) {
    return this.request(`/api/klienter/${klientId}`);
  }

  async createKlient(klientData) {
    return this.request('/api/klienter', {
      method: 'POST',
      body: JSON.stringify(klientData),
    });
  }

  async updateKlient(klientId, klientData) {
    return this.request(`/api/klienter/${klientId}`, {
      method: 'PUT',
      body: JSON.stringify(klientData),
    });
  }

  async deleteKlient(klientId) {
    return this.request(`/api/klienter/${klientId}`, {
      method: 'DELETE',
    });
  }

  // ============================================
  // TIDSLINJE
  // ============================================

  async getTidslinje(sakId) {
    return this.request(`/api/saker/${sakId}/tidslinje`);
  }

  async addTidslinjePost(sakId, post) {
    return this.request(`/api/saker/${sakId}/tidslinje`, {
      method: 'POST',
      body: JSON.stringify(post),
    });
  }

  // ============================================
  // FRISTER
  // ============================================

  async getFrister(sakId = null) {
    const endpoint = sakId ? `/api/saker/${sakId}/frister` : '/api/frister';
    return this.request(endpoint);
  }

  async createFrist(sakId, fristData) {
    return this.request(`/api/saker/${sakId}/frister`, {
      method: 'POST',
      body: JSON.stringify(fristData),
    });
  }

  async updateFrist(fristId, fristData) {
    return this.request(`/api/frister/${fristId}`, {
      method: 'PUT',
      body: JSON.stringify(fristData),
    });
  }

  async deleteFrist(fristId) {
    return this.request(`/api/frister/${fristId}`, {
      method: 'DELETE',
    });
  }

  // ============================================
  // SHAREPOINT
  // ============================================

  async getSharePointMapper() {
    return this.request('/api/sharepoint/saksmapper');
  }

  async getSharePointMappe(saksnummer) {
    return this.request(`/api/sharepoint/saksmapper/${saksnummer}`);
  }

  async createSharePointMappe(saksnummer, klientNavn, tittel = null) {
    return this.request('/api/sharepoint/saksmapper', {
      method: 'POST',
      body: JSON.stringify({ saksnummer, klientNavn, tittel }),
    });
  }

  // ============================================
  // BUDSJETT-VARSLING
  // ============================================

  async getBudsjettStatus() {
    return this.request('/api/budsjett/status');
  }

  async getBudsjettDashboardWidget() {
    return this.request('/api/budsjett/dashboard-widget');
  }

  async sendBudsjettVarsel(sakId, mottakerEpost = null) {
    return this.request('/api/budsjett/send-varsel', {
      method: 'POST',
      body: JSON.stringify({ sak_id: sakId, mottaker_epost: mottakerEpost }),
    });
  }

  // ============================================
  // OPPGAVER
  // ============================================

  async getOppgaver(filters = {}) {
    const params = new URLSearchParams(filters);
    return this.request(`/api/oppgaver/v2?${params}`);
  }

  async getOppgave(oppgaveId) {
    return this.request(`/api/oppgaver/v2?id=${oppgaveId}`);
  }

  async createOppgave(oppgaveData) {
    return this.request('/api/oppgaver/v2', {
      method: 'POST',
      body: JSON.stringify(oppgaveData),
    });
  }

  async updateOppgave(oppgaveId, oppgaveData) {
    return this.request(`/api/oppgaver/v2/${oppgaveId}`, {
      method: 'PUT',
      body: JSON.stringify(oppgaveData),
    });
  }

  async deleteOppgave(oppgaveId) {
    return this.request(`/api/oppgaver/v2/${oppgaveId}`, {
      method: 'DELETE',
    });
  }

  // ============================================
  // E-POST OPPFØLGINGSFORSLAG
  // ============================================

  async getOppfolgingsforslag(epostId) {
    return this.request(`/api/epost/${epostId}/oppfolgingsforslag`);
  }

  async godkjennForslag(forslagId, justeringer = {}) {
    return this.request(`/api/epost-forslag/${forslagId}/godkjenn`, {
      method: 'POST',
      body: JSON.stringify(justeringer),
    });
  }

  async justerForslag(forslagId, endringer) {
    return this.request(`/api/epost-forslag/${forslagId}`, {
      method: 'PUT',
      body: JSON.stringify(endringer),
    });
  }

  async avvisForslag(forslagId) {
    return this.request(`/api/epost-forslag/${forslagId}`, {
      method: 'DELETE',
    });
  }

  async godkjennAlleForslag(emailId) {
    return this.request(`/api/epost-forslag/godkjenn-alle/${emailId}`, {
      method: 'POST',
    });
  }

  // ============================================
  // SØKNAD
  // ============================================

  async searchAll(query) {
    return this.request(`/api/search?q=${encodeURIComponent(query)}`);
  }
}

// Singleton instance
const crmApi = new CRMApi();

export default crmApi;
